import Vue from 'vue';
import { MetaInfo } from 'vue-meta';
import { Component } from 'nuxt-property-decorator';
import { RawLocation } from 'vue-router';
import { HIcon, HImg, HSelect, HSelectItem, IconName } from '~/components';
import { GFError } from '~/error';
import { AvailableLanguage, SpecialPageListMetaInfoLS } from '~/schemes';
import {
  RawIncomingSearchSpecialPageBySpaceParams,
  SearchSpecialPageBySpaceResponse,
  SpecialPageSummary,
} from '~/plugins/gf-api/client/@types';
import { generateHreflang } from '~/helpers';

/** 1ページに表示する特集ページ件数 */
const DEFAULT_LIMIT = 9;
/** 全てのブランド選択時の値 */
const NOT_SELECT_BRAND_VALUE = '';
const PageLinkTypes = ['prev', 'next'] as const;
type PageLinkType = typeof PageLinkTypes[number];
const PAGE_LINK_ICON_MAP: Record<PageLinkType, IconName> = {
  prev: 'arrow-head-left',
  next: 'arrow-head-right',
};

interface SpecialPageSearchParams
  extends Pick<
    RawIncomingSearchSpecialPageBySpaceParams,
    'limit' | 'page' | 'filter'
  > {}
interface PagingItem {
  value: number | string;
  active: boolean;
  to: RawLocation;
}

const extractSearchParams = (
  query: Record<string, string | (string | null)[]>,
): SpecialPageSearchParams => {
  const limit =
    typeof query.limit === 'string' ? Number(query.limit) : DEFAULT_LIMIT;
  const page = typeof query.page === 'string' ? Number(query.page) : undefined;
  const brand = typeof query.brand === 'string' ? query.brand : undefined;
  const filter = brand ? { space: 'brand', target: brand } : undefined;
  const params: SpecialPageSearchParams = {
    limit,
    page,
  };
  if (filter) {
    params.filter = JSON.stringify(filter);
  }
  return params;
};

interface NormalizedSearchParams
  extends Pick<SearchSpecialPageBySpaceResponse['params'], 'limit' | 'page'> {
  brand: string;
}

/** 言語毎の特集一覧メタ情報 */
const META_INFO_LS: SpecialPageListMetaInfoLS = {
  title: {
    ja: '旅を提案する特集一覧｜星野リゾート【公式】',
    en: 'Travel Articles｜Hoshino Resorts Official Website',
    ko: '여행 게시글｜호시노 리조트',
    zh_cn: '旅行文章｜星野集团官方网站',
    zh_tw: '旅遊文章｜星野集團官方網站',
  },
  siteName: {
    ja: '星野リゾート【公式】',
    en: 'Hoshino Resorts Official Website | Hotels & Resorts',
    ko: '호시노 리조트 | 호텔 ＆ 리조트',
    zh_cn: '星野集团官方网站 | 酒店＆度假胜地',
    zh_tw: '星野集團官方網站 | 酒店 & 度假村',
  },
  description: {
    ja:
      '星野リゾートならではの滞在の楽しみ方や、家族旅、愛犬旅、ひとり旅など様々な旅の提案を行う特集を一覧でご紹介しています。ご予約は当サイトが最もお得です。',
    en:
      'Discover an array of exclusive articles tailored to elevate your experience at Hoshino Resorts,  for unforgettable family getaways, pet-friendly excursions, solo adventures, and more. Unlock the best rates by booking directly with us.',
    ko:
      '호시노 리조트에서 잊을 수 없는 가족 여행, 반려동물 동반 여행, 나홀로 모험 등 호시노 리조트에서의 경험을 향상시킬 수 있는 다양한 독점 상품을 만나보세요. 직접 예약하여 최적의 요금을 누려보세요.',
    zh_cn:
      '发现一系列专为提升您在星野度假村的体验而量身定制的独家文章，为您带来难忘的家庭度假、宠物友好游、独自探险等体验。直接向我们预订，即可享受最优惠的价格。',
    zh_tw:
      '發現一係列專為提升您在星野度假村的體驗而量身定制的獨家文章，為難忘的家庭度假，寵物友好的遠足，單人冒險等。通過直接與我們預訂來解鎖最佳價格。',
  },
};

@Component<PrioritySpecialPageView>({
  scrollToTop: true,
  watchQuery: ['limit', 'page', 'brand'],
  async asyncData({ $error, redirect, $i18n, query, $gfAPI }) {
    try {
      const searchQuery = extractSearchParams(query);

      const res = await $gfAPI.special_pages.search_by_space.$get({
        query: searchQuery,
      });

      return { res };
    } catch (_err) {
      const err = GFError.from(_err);
      if (err.status === 404) {
        const corporateSiteURL = $i18n.t('url.globalTop') as string;

        return redirect(corporateSiteURL);
      }
      $error.throw(err);
    }
  },
  head() {
    // 言語に紐づくメタ情報を取得する
    const currentLanguage = this.$language.current;
    const {
      title,
      siteName,
      description,
    } = this.extractSpecialPageListMetaInfoLS(META_INFO_LS, currentLanguage);

    const head: MetaInfo = {
      titleTemplate: [this.title, siteName].join('｜'),
      meta: [
        { hid: 'og:title', property: 'og:title', content: title },
        {
          hid: 'og:description',
          property: 'og:description',
          content: description,
        },
        { hid: 'og:site_name', property: 'og:site_name', content: siteName },
        {
          hid: 'og:url',
          property: 'og:url',
          content: `${this.$navigation.origin}/${currentLanguage}/sp/`,
        },
        {
          hid: 'og:image',
          property: 'og:image',
          content: this.$res.img(
            `/special-page-list/ogp-${currentLanguage}.png`,
          ),
        },
        {
          hid: 'og:type',
          property: 'og:type',
          content: 'article',
        },
        // Twitter
        {
          hid: 'twitter:site',
          property: 'twitter:site',
          content: '@HoshinoResorts',
        },
        {
          hid: 'twitter:title',
          property: 'twitter:title',
          content: title,
        },
        {
          hid: 'twitter:image',
          property: 'twitter:image',
          content: this.$res.img(
            `/special-page-list/twittercard-ogp-${currentLanguage}.png`,
          ),
        },
        // Facebook
        {
          hid: 'fb:app_id',
          property: 'fb:app_id',
          content: '294558690586688',
        },
      ],
      link: generateHreflang(this),
    };

    return head;
  },
  render() {
    const { res, brands, renderPageLink } = this;
    const { filter, page } = res.params;

    return (
      <div
        staticClass="priority-special-page-index-view"
        key={`${filter.space}:${filter.target}:${page}`}>
        <h1
          staticClass="priority-special-page-index-view__title"
          domPropsInnerHTML={this.$t('label.travelArticles') as string}></h1>
        <HSelect
          staticClass="priority-special-page-index-view__filter"
          items={brands}
          color="default"
          inputValue={this.searchParams.brand}
          onInput={this.handleSelectBrand}
        />
        {!!res.hits.length && (
          <ul staticClass="priority-special-page-index-view__items">
            {res.hits.map((specialPage) => (
              <li staticClass="priority-special-page-index-view__item">
                <a
                  href={this.getSpecialPageLink(specialPage)}
                  staticClass="priority-special-page-index-view__item__link">
                  {/* 画像があれば表示し、なければグレーの四角形を表示 */}
                  {specialPage.thumbnail?.public_id ? (
                    <HImg
                      staticClass="priority-special-page-index-view__item__image"
                      src={this.$res.img(
                        specialPage.thumbnail?.derived.main.url!,
                      )}
                    />
                  ) : (
                    <div
                      class={[
                        'priority-special-page-index-view__item__empty-image',
                      ]}></div>
                  )}
                  {specialPage.heading && (
                    <h3
                      staticClass="priority-special-page-index-view__item__caption"
                      v-wysiwyg={specialPage.heading}></h3>
                  )}
                </a>
              </li>
            ))}
          </ul>
        )}
        {this.paging.items.length > 1 && (
          <div staticClass="priority-special-page-index-view-paging">
            {/* 前ページ */}
            {renderPageLink('prev')}
            {/* ページング */}
            {this.paging.items.map((page) => (
              <nuxt-link
                key={`${page.value}:active`}
                staticClass="priority-special-page-index-view-paging__item"
                class={{
                  'priority-special-page-index-view-paging__item--active':
                    page.active,
                }}
                to={page.to}>
                {page.value}
              </nuxt-link>
            ))}
            {/* 次ページ */}
            {renderPageLink('next')}
          </div>
        )}
      </div>
    );
  },
})
export default class PrioritySpecialPageView extends Vue {
  readonly res!: SearchSpecialPageBySpaceResponse;

  get title() {
    return this.$t('name.specialPageList') as string;
  }

  get totalPages(): number {
    const { total, params } = this.res;
    const { limit } = params;
    return Math.ceil(total / limit);
  }

  get paging() {
    const { total, hits, params } = this.res;
    const { limit, page } = params;
    const offsetFrom = (page - 1) * limit + 1;
    const offsetTo = offsetFrom + hits.length - 1;
    const items: PagingItem[] = [];
    const totalPages = this.totalPages;
    // 最大表示ページ数
    const maxDisplayedPages = 8;

    // 現在のページを中心に最大表示ページ数を表示する
    const startPage = Math.max(
      1,
      Math.min(
        page <= maxDisplayedPages ? page - maxDisplayedPages : page,
        totalPages - maxDisplayedPages + 1,
      ),
    );

    const endPage = Math.min(
      totalPages,
      page < maxDisplayedPages
        ? maxDisplayedPages
        : startPage + maxDisplayedPages - 1,
    );

    // 最初のページ
    if (startPage > 1) {
      items.push({
        value: 1,
        active: false,
        to: this.createMergedSearchLocation({ page: 1 }),
      });
      if (startPage > maxDisplayedPages) {
        items.push({
          value: '...',
          active: false,
          to: this.createMergedSearchLocation({ page: startPage - 1 }),
        });
      }
    }

    // 中間ページ
    for (let value = startPage; value <= endPage; value++) {
      items.push({
        value,
        active: value === page,
        to: this.createMergedSearchLocation({ page: value }),
      });
    }

    // 最後のページ
    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        items.push({
          value: '...',
          active: false,
          to: this.createMergedSearchLocation({ page: endPage + 1 }),
        });
      }
      items.push({
        value: totalPages,
        active: false,
        to: this.createMergedSearchLocation({ page: totalPages }),
      });
    }

    return {
      total,
      from: offsetFrom,
      to: offsetTo,
      items,
    };
  }

  get searchParams(): NormalizedSearchParams {
    return {
      limit: this.res.params.limit,
      page: this.res.params.page,
      brand: this.res.params.filter.target || '',
    };
  }

  /** ブランドプルダウンリスト */
  get brands(): HSelectItem[] {
    return [
      {
        label: this.$t('label.viewAllBrand') as string,
        value: NOT_SELECT_BRAND_VALUE,
      },
      ...this.$hotel.brands.map(({ slug, name }) => ({
        label: name,
        value: slug,
      })),
    ];
  }

  /**
   * 有効言語に紐づくメタ情報を取得する
   * @param metaInfoLs 言語毎のメタ情報
   * @param lang 有効言語
   * @returns 有効言語に紐づくメタ情報
   */
  private extractSpecialPageListMetaInfoLS(
    metaInfoLs: SpecialPageListMetaInfoLS,
    lang: AvailableLanguage,
  ): Record<keyof SpecialPageListMetaInfoLS, string> {
    return {
      title: metaInfoLs.title[lang] as string,
      siteName: metaInfoLs.siteName[lang] as string,
      description: metaInfoLs.description[lang] as string,
    };
  }

  private renderPageLink(pageLinkType: PageLinkType) {
    const { page } = this.res.params;
    return (
      <nuxt-link
        staticClass={`priority-special-page-index-view-paging__${pageLinkType}`}
        class={{
          [`priority-special-page-index-view-paging__${pageLinkType}--disabled`]:
            pageLinkType === 'prev' ? page === 1 : page === this.totalPages,
        }}
        to={this.createMergedSearchLocation({
          page: pageLinkType === 'prev' ? page - 1 : page + 1,
        })}>
        <HIcon name={PAGE_LINK_ICON_MAP[pageLinkType]} />
      </nuxt-link>
    );
  }

  private getSpecialPageLink(specialPage: SpecialPageSummary): string {
    const link = '';
    const lang = this.$language.current;
    const { space, target, slug } = specialPage;
    if (space === 'global') {
      return `/${lang}/sp/${slug}`;
    }

    if (space === 'brand') {
      const brand = this.$hotel.brands.find(({ id }) => id === target);
      return `/${lang}/brands/${brand?.slug}/sp/${slug}`;
    }

    if (space === 'hotel') {
      const hotel = this.$hotel.all.find(({ id }) => id === target);
      return `/${lang}/hotels/${hotel?.slug}/sp/${slug}`;
    }
    return link;
  }

  private createMergedSearchQuery(params: Partial<NormalizedSearchParams>) {
    const newParams = { ...this.searchParams, ...params };
    if (newParams.brand !== this.searchParams.brand) {
      newParams.page = 1;
    }
    const newQuery: Record<string, string> = {
      limit: String(newParams.limit),
    };
    if (newParams.page) {
      newQuery.page = String(newParams.page);
    }
    if (newParams.brand !== undefined) {
      newQuery.brand = newParams.brand;
    }
    const query = {
      ...this.$route.query,
      ...newQuery,
    };
    if (query.page === '1') {
      delete query.page;
    }
    if (query.limit === String(DEFAULT_LIMIT)) {
      delete query.limit;
    }
    if (query.brand === NOT_SELECT_BRAND_VALUE) {
      delete query.brand;
    }
    return query;
  }

  private createMergedSearchLocation(params: Partial<NormalizedSearchParams>) {
    return {
      path: this.$route.path,
      query: this.createMergedSearchQuery(params),
    };
  }

  private pushSearchParams(params: Partial<NormalizedSearchParams>) {
    return this.$router.push(this.createMergedSearchLocation(params));
  }

  /**
   * ブランドプルダウン変更時のハンドラ
   * @param brand ブランドURLスラッグ
   */
  private handleSelectBrand(brand: string) {
    return this.pushSearchParams({ brand });
  }
}
